import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { 
  collection, 
  getDocs, 
  query,
  setDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  getAuth
} from 'firebase/auth';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { db } from '../config/firebase';
import { FirebaseUser, DeductionItem } from '../config/types';
import { useAppSelector } from '../store';
import { Navigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';

const ACCOUNT_TYPES = {
  admin: 'Admin',
  owner: 'Owner',
  driver: 'Driver'
} as const;

type AccountType = keyof typeof ACCOUNT_TYPES;

const AdminUserManagement: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    dogtag: '',
    admin: false,
    driver: false,
    accountType: 'driver' as AccountType,
    percentageCut: 0,
    monthlyDeductions: [] as DeductionItem[],
  });

  // State for deduction dialogs
  const [deductionDialogOpen, setDeductionDialogOpen] = useState(false);
  const [editDeductionsDialogOpen, setEditDeductionsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<FirebaseUser | null>(null);
  const [editingDeductions, setEditingDeductions] = useState<DeductionItem[]>([]);
  const [newDeduction, setNewDeduction] = useState({
    name: '',
    amount: '',
    description: '',
  });
  const [editPercentageCut, setEditPercentageCut] = useState(0);

  // Fetch existing users
  const fetchUsers = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const querySnapshot = await getDocs(usersQuery);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FirebaseUser[];
      setUsers(usersData);
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent) => {
    const { name, value, checked } = e.target as HTMLInputElement;
    if (name === 'admin' || name === 'driver') {
      setNewUser(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'percentageCut') {
      const percentage = Math.min(Math.max(parseFloat(value as string) || 0, 0), 100);
      setNewUser(prev => ({
        ...prev,
        [name]: percentage
      }));
    } else if (name === 'accountType') {
      setNewUser(prev => ({
        ...prev,
        [name]: value as AccountType,
        admin: value === 'admin',
        driver: value === 'driver'
      }));
    } else {
      setNewUser(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDeductionDialogOpen = () => {
    setDeductionDialogOpen(true);
  };

  const handleDeductionDialogClose = () => {
    setDeductionDialogOpen(false);
    setNewDeduction({ name: '', amount: '', description: '' });
  };

  const handleAddDeduction = () => {
    if (newDeduction.name && newDeduction.amount) {
      setNewUser(prev => ({
        ...prev,
        monthlyDeductions: [
          ...prev.monthlyDeductions,
          {
            name: newDeduction.name,
            amount: parseFloat(newDeduction.amount),
            description: newDeduction.description || undefined,
          },
        ],
      }));
      handleDeductionDialogClose();
    }
  };

  const handleRemoveDeduction = (index: number) => {
    setNewUser(prev => ({
      ...prev,
      monthlyDeductions: prev.monthlyDeductions.filter((_, i) => i !== index),
    }));
  };

  const handleEditDeductions = (userToEdit: FirebaseUser) => {
    setSelectedUser(userToEdit);
    setEditingDeductions(userToEdit.monthlyDeductions || []);
    setEditPercentageCut(userToEdit.percentageCut || 0);
    setEditDeductionsDialogOpen(true);
  };

  const handleEditDeductionsClose = () => {
    setEditDeductionsDialogOpen(false);
    setSelectedUser(null);
    setEditingDeductions([]);
    setEditPercentageCut(0);
  };

  const handleAddEditingDeduction = () => {
    if (newDeduction.name && newDeduction.amount) {
      setEditingDeductions(prev => [
        ...prev,
        {
          name: newDeduction.name,
          amount: parseFloat(newDeduction.amount),
          description: newDeduction.description || undefined,
        },
      ]);
      setNewDeduction({ name: '', amount: '', description: '' });
    }
  };

  const handleRemoveEditingDeduction = (index: number) => {
    setEditingDeductions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveDeductions = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'users', selectedUser.id), {
        monthlyDeductions: editingDeductions,
        percentageCut: editPercentageCut,
      });

      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === selectedUser.id 
          ? { ...u, monthlyDeductions: editingDeductions, percentageCut: editPercentageCut }
          : u
      ));

      setSuccess('Deductions updated successfully');
      handleEditDeductionsClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update deductions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate inputs
      if (!newUser.name || !newUser.dogtag || !newUser.email || !newUser.password || !newUser.phone) {
        throw new Error('Please fill in all required fields');
      }

      // Create authentication user
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );

      const uid = userCredential.user.uid;

      // Add user data to Firestore using the auth UID
      await setDoc(doc(db, 'users', uid), {
        id: uid,
        name: newUser.name,
        email: newUser.email,
        phone: newUser.phone,
        dogtag: newUser.dogtag,
        admin: newUser.accountType === 'admin',
        driver: newUser.accountType === 'driver',
        accountType: newUser.accountType,
        percentageCut: newUser.percentageCut,
        monthlyDeductions: newUser.monthlyDeductions,
        createdAt: new Date().toISOString(),
      });

      // Reset form
      setNewUser({
        name: '',
        email: '',
        password: '',
        phone: '',
        dogtag: '',
        admin: false,
        driver: false,
        accountType: 'driver',
        percentageCut: 0,
        monthlyDeductions: [],
      });
      setSuccess('User added successfully');
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to add user');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccountType = async (userId: string, newAccountType: AccountType) => {
    setLoading(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        accountType: newAccountType,
        admin: newAccountType === 'admin',
        driver: newAccountType === 'driver'
      });

      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === userId 
          ? { ...u, accountType: newAccountType, admin: newAccountType === 'admin', driver: newAccountType === 'driver' }
          : u
      ));

      setSuccess('Account type updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update account type');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is admin, if not redirect to home
  if (!user?.uid) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>

        {/* Add New User Form */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Add New User
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={newUser.name}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={newUser.email}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={newUser.password}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              label="Phone Number"
              name="phone"
              type="tel"
              value={newUser.phone}
              onChange={handleInputChange}
              placeholder="(123) 456-7890"
            />
            <TextField
              required
              fullWidth
              label="Dogtag"
              name="dogtag"
              value={newUser.dogtag}
              onChange={handleInputChange}
            />
            {/* Percentage Cut Field */}
            <TextField
              required
              fullWidth
              label="Percentage Cut"
              name="percentageCut"
              type="number"
              value={newUser.percentageCut}
              onChange={handleInputChange}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Percentage to deduct from each load (0-100)"
            />
            {/* Monthly Deductions Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Monthly Deductions
              </Typography>
              <List>
                {newUser.monthlyDeductions.map((deduction, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={deduction.name}
                      secondary={`$${deduction.amount.toFixed(2)}${deduction.description ? ` - ${deduction.description}` : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveDeduction(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Button
                startIcon={<AddIcon />}
                onClick={handleDeductionDialogOpen}
                variant="outlined"
                size="small"
              >
                Add Deduction
              </Button>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                name="accountType"
                value={newUser.accountType}
                    onChange={handleInputChange}
                label="Account Type"
              >
                {Object.entries(ACCOUNT_TYPES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add User
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </Paper>

        {/* Users List */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Existing Users
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Dogtag</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>% Cut</TableCell>
                  <TableCell>Monthly Deductions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>{user.dogtag}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={user.accountType || 'driver'}
                          onChange={(e) => handleUpdateAccountType(user.id, e.target.value as AccountType)}
                          size="small"
                        >
                          {Object.entries(ACCOUNT_TYPES).map(([value, label]) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>{user.percentageCut}%</TableCell>
                    <TableCell>
                      {user.monthlyDeductions?.length > 0 ? (
                        <Typography variant="body2">
                          Total: ${user.monthlyDeductions.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
                        </Typography>
                      ) : 'None'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Deductions">
                        <IconButton onClick={() => handleEditDeductions(user)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Add Deduction Dialog */}
      <Dialog open={deductionDialogOpen} onClose={handleDeductionDialogClose}>
        <DialogTitle>Add Monthly Deduction</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Deduction Name"
              value={newDeduction.name}
              onChange={(e) => setNewDeduction(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={newDeduction.amount}
              onChange={(e) => setNewDeduction(prev => ({ ...prev, amount: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newDeduction.description}
              onChange={(e) => setNewDeduction(prev => ({ ...prev, description: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeductionDialogClose}>Cancel</Button>
          <Button onClick={handleAddDeduction} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Deductions Dialog */}
      <Dialog 
        open={editDeductionsDialogOpen} 
        onClose={handleEditDeductionsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Deductions for {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Percentage Cut"
              type="number"
              value={editPercentageCut}
              onChange={(e) => setEditPercentageCut(Math.min(Math.max(parseFloat(e.target.value) || 0, 0), 100))}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Percentage to deduct from each load (0-100)"
            />
            
            <Typography variant="subtitle1">Monthly Deductions</Typography>
            
            <List>
              {editingDeductions.map((deduction, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={deduction.name}
                    secondary={`$${deduction.amount.toFixed(2)}${deduction.description ? ` - ${deduction.description}` : ''}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleRemoveEditingDeduction(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography variant="subtitle2">Add New Deduction</Typography>
              <TextField
                fullWidth
                label="Deduction Name"
                value={newDeduction.name}
                onChange={(e) => setNewDeduction(prev => ({ ...prev, name: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={newDeduction.amount}
                onChange={(e) => setNewDeduction(prev => ({ ...prev, amount: e.target.value }))}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                fullWidth
                label="Description (Optional)"
                value={newDeduction.description}
                onChange={(e) => setNewDeduction(prev => ({ ...prev, description: e.target.value }))}
              />
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddEditingDeduction}
                variant="outlined"
                disabled={!newDeduction.name || !newDeduction.amount}
              >
                Add Deduction
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDeductionsClose}>Cancel</Button>
          <Button 
            onClick={handleSaveDeductions} 
            variant="contained"
            disabled={loading}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUserManagement; 